import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { AssetDto } from '@picflow/types';
import { Rect } from 'src/app/classes/rect.class';
import { assetSideMenuSize } from 'src/app/consts';
import { devicePixelRatio } from 'src/app/lib/device-pixel-ratio';
import { GetBounds } from 'src/app/lib/get-bounds';
import { lerp } from 'src/app/lib/lerp-array';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { ResizeDirective } from '../../../shared/directives/resize.directive';
import { AnnotationColorToHex } from '../../types/annotation-color';
import { Shape } from '../../types/shapes';

@Component({
  selector: 'app-view-annotation',
  templateUrl: './view-annotation.component.html',
  styleUrls: ['./view-annotation.component.scss'],
  standalone: true,
  imports: [ResizeDirective, ButtonDirective, SvgComponent],
})
export class ViewAnnotationComponent implements AfterViewInit, OnDestroy, OnChanges {
  @Input() sideMenuOpen: boolean;
  @Input() shapes: Shape[];
  @Input() scale: number;
  @Input() offsetScale: number;
  @Input() offsetX: number;
  @Input() offsetY: number;
  @Input() asset: AssetDto;
  @Input() noAnimate: boolean;
  @Input() faded: boolean;
  @Output() onClose = new EventEmitter();
  private destroyed = false;
  @ViewChild('canvas') private canvasEl: ElementRef<HTMLCanvasElement>;
  private ctx: CanvasRenderingContext2D;
  transitionStartTime: number;
  startVals: { bounds: Rect; scale: number };
  vals: { bounds: Rect; scale: number };

  async ngOnChanges() {
    if (!this.noAnimate) {
      this.transitionStartTime = Date.now();
      this.startVals = this.vals;
    }
  }

  ngAfterViewInit() {
    this.ctx = this.canvasEl.nativeElement.getContext('2d');
    this.vals = this.toVals();
    this.startVals = this.vals;
    window.requestAnimationFrame(() => this.draw());
  }

  toVals() {
    const bounds = GetBounds(this.offsetScale, this.scale, this.sideMenuOpen, this.asset, this.offsetX, this.offsetY);
    return { bounds, scale: this.scale };
  }

  private getVals(): { bounds: Rect; scale: number } {
    if (this.noAnimate || !this.transitionStartTime) return this.vals;

    let progress = (1 / 200) * (Date.now() - this.transitionStartTime);
    if (progress >= 1) {
      this.transitionStartTime = undefined;
      progress = 1;
    }
    const oldValsArray = [
      this.startVals.bounds.x,
      this.startVals.bounds.y,
      this.startVals.bounds.width,
      this.startVals.bounds.height,
      this.startVals.scale,
    ];
    const newValsArray = [
      this.vals.bounds.x,
      this.vals.bounds.y,
      this.vals.bounds.width,
      this.vals.bounds.height,
      this.vals.scale,
    ];

    const lerpedValsArray = lerp(oldValsArray, newValsArray, progress);

    return {
      bounds: new Rect(lerpedValsArray[0], lerpedValsArray[1], lerpedValsArray[2], lerpedValsArray[3]),
      scale: lerpedValsArray[4],
    };
  }

  draw() {
    if (this.destroyed) return;
    this.canvasEl.nativeElement.width =
      (window.innerWidth - (this.sideMenuOpen ? assetSideMenuSize : 0)) * devicePixelRatio();
    this.canvasEl.nativeElement.height = window.innerHeight * devicePixelRatio();

    this.vals = this.toVals();
    const vals = this.getVals();
    const bounds = vals.bounds.times(devicePixelRatio());
    this.ctx.globalAlpha = 0.9;
    this.ctx.lineWidth = 6 * devicePixelRatio();
    this.ctx.lineCap = 'round';
    this.ctx.lineJoin = 'round';

    this.shapes.forEach(shape => {
      this.ctx.strokeStyle = this.getColor(shape.color);
      this.ctx.fillStyle = this.ctx.strokeStyle;
      this.ctx.beginPath();

      if (shape.tool === 'polygon') {
        if (shape.points.length > 2) {
          this.ctx.moveTo(bounds.x + shape.points[0] * bounds.width, bounds.y + shape.points[1] * bounds.height);
          for (let i = 2; i < shape.points.length; i += 2) {
            const x = bounds.x + shape.points[i] * bounds.width;
            const y = bounds.y + shape.points[i + 1] * bounds.height;
            this.ctx.lineTo(x, y);
          }
          this.ctx.stroke();
        } else {
          const x = bounds.x + shape.points[0] * bounds.width;
          const y = bounds.y + shape.points[1] * bounds.height;
          this.ctx.arc(x, y, 5, 0, 2 * Math.PI);
          this.ctx.fill();
        }
        this.ctx.closePath();
      } else if (shape.tool === 'oval') {
        let x = bounds.x + shape.x1 * bounds.width;
        let y = bounds.y + shape.y1 * bounds.height;
        let sizeX = shape.x2 - shape.x1;
        sizeX = sizeX * vals.scale * this.offsetScale * this.asset.version_data.data.width * devicePixelRatio();

        let sizeY = shape.y2 - shape.y1;
        sizeY = sizeY * vals.scale * this.offsetScale * this.asset.version_data.data.height * devicePixelRatio();

        if (sizeX > 0) {
          x = x + sizeX / 2;
        } else {
          sizeX = -sizeX;
          x = x - sizeX / 2;
        }
        if (sizeY > 0) {
          y = y + sizeY / 2;
        } else {
          sizeY = -sizeY;
          y = y - sizeY / 2;
        }

        this.ctx.ellipse(x, y, sizeX / 2, sizeY / 2, 0, 0, 2 * Math.PI);
        this.ctx.stroke();
      } else if (shape.tool === 'rect') {
        const x = bounds.x + shape.x1 * bounds.width;
        const y = bounds.y + shape.y1 * bounds.height;
        let sizeX = shape.x2 - shape.x1;
        sizeX = sizeX * vals.scale * this.offsetScale * this.asset.version_data.data.width * devicePixelRatio();

        let sizeY = shape.y2 - shape.y1;
        sizeY = sizeY * vals.scale * this.offsetScale * this.asset.version_data.data.height * devicePixelRatio();

        this.ctx.rect(x, y, sizeX, sizeY);
        this.ctx.stroke();
      } else if (shape.tool === 'line') {
        const x1 = bounds.x + shape.x1 * bounds.width;
        const y1 = bounds.y + shape.y1 * bounds.height;
        const x2 = bounds.x + shape.x2 * bounds.width;
        const y2 = bounds.y + shape.y2 * bounds.height;

        this.ctx.moveTo(x1, y1);
        this.ctx.lineTo(x2, y2);
        this.ctx.stroke();
      } else if (shape.tool === 'arrow') {
        const x1 = bounds.x + shape.x1 * bounds.width;
        const y1 = bounds.y + shape.y1 * bounds.height;
        const x2 = bounds.x + shape.x2 * bounds.width;
        const y2 = bounds.y + shape.y2 * bounds.height;
        const diff = new Rect(x2, y2).subtract(new Rect(x1, y1));
        const angle = diff.toRadians() - Math.PI / 2;
        let dist = diff.magnitude();
        if (dist === 0) return;
        let extraDist = 0 - dist;
        if (extraDist < 0) extraDist = 0;
        dist += extraDist;

        this.ctx.save();
        this.ctx.translate(x1, y1);
        this.ctx.rotate(angle);

        this.ctx.translate(0, -extraDist);

        const triangleSides = 28;
        const triangleLength = 30;

        this.ctx.moveTo(0, 0);
        this.ctx.lineTo(0, dist);
        this.ctx.moveTo(-triangleSides, dist - triangleLength);
        this.ctx.lineTo(0, dist);
        this.ctx.lineTo(triangleSides, dist - triangleLength);

        this.ctx.stroke();
        this.ctx.closePath();
        this.ctx.restore();
      }
    });
    window.requestAnimationFrame(() => this.draw());
  }

  private getColor(color: string): string {
    return AnnotationColorToHex(color);
  }

  ngOnDestroy() {
    this.destroyed = true;
  }
}
